import type {
  DocumentTypeInput,
  EscalationLevelInput,
  EscalationLevelView,
  JobReasonCodeInput,
  JobReasonCodeView,
  JobTypeFamily,
  JobTypeInput,
  RequiredDocumentTypeInput,
  TrailerBodyStyle,
  TrailerTypeInput,
  TruckBodyStyle,
  TruckClassInput,
  ZoneKindColor,
  ZoneKindInput,
  TrailerHitch,
  TruckCoupling,
} from '@chukua/common';
import { api } from '../../shared/shared.api';

export interface TruckClass {
  id: string;
  code: string;
  name: string;
  typicalCapacityTonnes: number;
  requiredLicenceClass: string | null;
  /** What the class looks like behind the cab, for the tyre layout's 3D model. */
  bodyStyle: TruckBodyStyle;
  /** What the class can pull (gitaumoses4/chukua#390). */
  coupling: TruckCoupling;
  /** Km per litre a truck of this class starts from until it has trips of its own (gitaumoses4/chukua#110). */
  defaultKmPerLitre: number | null;
  isActive: boolean;
}

export interface TrailerType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  /** Container sizes this trailer type can carry (gitaumoses4/chukua#48). */
  containerSizeCodes: string[];
  /** What the type is built as, for the tyre layout's 3D model. */
  bodyStyle: TrailerBodyStyle;
  /** How the type hitches to its truck (gitaumoses4/chukua#390). */
  hitch: TrailerHitch;
}

export interface DocumentType {
  id: string;
  code: string;
  name: string;
  appliesTo: 'truck' | 'trailer' | 'driver';
  legalHardBlock: boolean;
  crossBorderOnly: boolean;
  typicalValidityDays: number;
  reminderLeadDays: number;
  isActive: boolean;
}

export interface RequiredDocumentType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface JobType {
  id: string;
  code: string;
  name: string;
  family: JobTypeFamily;
  isActive: boolean;
  /** Licence classes the driver must hold for this job (gitaumoses4/chukua#48). */
  requiredLicenceClasses: string[];
  requiredDocuments: { id: string; code: string; name: string }[];
}

export function listTruckClasses(): Promise<TruckClass[]> {
  return api<TruckClass[]>('/truck-classes');
}

export function createTruckClass(input: TruckClassInput): Promise<TruckClass> {
  return api<TruckClass>('/truck-classes', { method: 'POST', body: JSON.stringify(input) });
}

export function updateTruckClass(id: string, input: TruckClassInput): Promise<TruckClass> {
  return api<TruckClass>(`/truck-classes/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateTruckClass(id: string): Promise<void> {
  await api<void>(`/truck-classes/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateTruckClass(id: string): Promise<void> {
  await api<void>(`/truck-classes/${id}/reactivate`, { method: 'POST' });
}

export function listTrailerTypes(): Promise<TrailerType[]> {
  return api<TrailerType[]>('/trailer-types');
}

export function createTrailerType(input: TrailerTypeInput): Promise<TrailerType> {
  return api<TrailerType>('/trailer-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateTrailerType(id: string, input: TrailerTypeInput): Promise<TrailerType> {
  return api<TrailerType>(`/trailer-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateTrailerType(id: string): Promise<void> {
  await api<void>(`/trailer-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateTrailerType(id: string): Promise<void> {
  await api<void>(`/trailer-types/${id}/reactivate`, { method: 'POST' });
}

export function listDocumentTypes(): Promise<DocumentType[]> {
  return api<DocumentType[]>('/document-types');
}

export function createDocumentType(input: DocumentTypeInput): Promise<DocumentType> {
  return api<DocumentType>('/document-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateDocumentType(id: string, input: DocumentTypeInput): Promise<DocumentType> {
  return api<DocumentType>(`/document-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateDocumentType(id: string): Promise<void> {
  await api<void>(`/document-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateDocumentType(id: string): Promise<void> {
  await api<void>(`/document-types/${id}/reactivate`, { method: 'POST' });
}

export function listRequiredDocumentTypes(): Promise<RequiredDocumentType[]> {
  return api<RequiredDocumentType[]>('/required-document-types');
}

export function createRequiredDocumentType(input: RequiredDocumentTypeInput): Promise<RequiredDocumentType> {
  return api<RequiredDocumentType>('/required-document-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateRequiredDocumentType(id: string, input: RequiredDocumentTypeInput): Promise<RequiredDocumentType> {
  return api<RequiredDocumentType>(`/required-document-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateRequiredDocumentType(id: string): Promise<void> {
  await api<void>(`/required-document-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateRequiredDocumentType(id: string): Promise<void> {
  await api<void>(`/required-document-types/${id}/reactivate`, { method: 'POST' });
}

export function listJobTypes(): Promise<JobType[]> {
  return api<JobType[]>('/job-types');
}

export function createJobType(input: JobTypeInput): Promise<Omit<JobType, 'requiredDocuments'>> {
  return api<Omit<JobType, 'requiredDocuments'>>('/job-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateJobType(id: string, input: JobTypeInput): Promise<Omit<JobType, 'requiredDocuments'>> {
  return api<Omit<JobType, 'requiredDocuments'>>(`/job-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateJobType(id: string): Promise<void> {
  await api<void>(`/job-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateJobType(id: string): Promise<void> {
  await api<void>(`/job-types/${id}/reactivate`, { method: 'POST' });
}

export interface ContainerSize {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface ContainerType {
  id: string;
  code: string;
  name: string;
  needsReeferPower: boolean;
  needsSkeletalOrFlatRack: boolean;
  isActive: boolean;
}

/** Read-only here: the catalogue itself is admin-managed (`configuration.manage`, gitaumoses4/chukua#23) — this reads it with `job.view` for the container job intake form. */
export function listContainerSizes(): Promise<ContainerSize[]> {
  return api<ContainerSize[]>('/container-sizes');
}

export function listContainerTypes(): Promise<ContainerType[]> {
  return api<ContainerType[]>('/container-types');
}

export interface WeightBand {
  id: string;
  code: string;
  name: string;
  minKg: number;
  maxKg: number | null;
  isActive: boolean;
}

/** Admin-only (`configuration.manage`): the one cargo vocabulary without a wider read gate, and the only roles that can author a line hold it too. */
export function listWeightBands(): Promise<WeightBand[]> {
  return api<WeightBand[]>('/weight-bands');
}

export interface SpecialHandlingRequirement {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

/** Read-only here: the catalogue itself is admin-managed (`configuration.manage`, gitaumoses4/chukua#32) — this reads it with `job.view` for the loose-cargo job intake form. */
export function listSpecialHandlingRequirements(): Promise<SpecialHandlingRequirement[]> {
  return api<SpecialHandlingRequirement[]>('/special-handling-requirements');
}

export interface ZoneKind {
  id: string;
  code: string;
  name: string;
  color: ZoneKindColor;
  isActive: boolean;
}

export function listZoneKinds(): Promise<ZoneKind[]> {
  return api<ZoneKind[]>('/zone-kinds');
}

export function createZoneKind(input: ZoneKindInput): Promise<ZoneKind> {
  return api<ZoneKind>('/zone-kinds', { method: 'POST', body: JSON.stringify(input) });
}

export function updateZoneKind(id: string, input: ZoneKindInput): Promise<ZoneKind> {
  return api<ZoneKind>(`/zone-kinds/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateZoneKind(id: string): Promise<void> {
  await api<void>(`/zone-kinds/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateZoneKind(id: string): Promise<void> {
  await api<void>(`/zone-kinds/${id}/reactivate`, { method: 'POST' });
}

export type JobReasonCode = JobReasonCodeView;

export function listJobReasonCodes(): Promise<JobReasonCode[]> {
  return api<JobReasonCode[]>('/job-reason-codes');
}

export function createJobReasonCode(input: JobReasonCodeInput): Promise<JobReasonCode> {
  return api<JobReasonCode>('/job-reason-codes', { method: 'POST', body: JSON.stringify(input) });
}

export function updateJobReasonCode(id: string, input: JobReasonCodeInput): Promise<JobReasonCode> {
  return api<JobReasonCode>(`/job-reason-codes/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateJobReasonCode(id: string): Promise<void> {
  await api<void>(`/job-reason-codes/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateJobReasonCode(id: string): Promise<void> {
  await api<void>(`/job-reason-codes/${id}/reactivate`, { method: 'POST' });
}

export type EscalationLevel = EscalationLevelView;

export function listEscalationLevels(): Promise<EscalationLevel[]> {
  return api<EscalationLevel[]>('/escalation-levels');
}

export function createEscalationLevel(input: EscalationLevelInput): Promise<EscalationLevel> {
  return api<EscalationLevel>('/escalation-levels', { method: 'POST', body: JSON.stringify(input) });
}

export function updateEscalationLevel(id: string, input: EscalationLevelInput): Promise<EscalationLevel> {
  return api<EscalationLevel>(`/escalation-levels/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateEscalationLevel(id: string): Promise<void> {
  await api<void>(`/escalation-levels/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateEscalationLevel(id: string): Promise<void> {
  await api<void>(`/escalation-levels/${id}/reactivate`, { method: 'POST' });
}
